import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { formatPlate, normalizePlate } from './utils/plateFormat';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import Landing from './Landing';
import Register from './Register';
import Login from './Login';
import AdminPage from './AdminPage';
import Terms from './Terms';

const API = '/api';

function getStored() {
  try {
    const t = localStorage.getItem('nasati_token');
    const u = localStorage.getItem('nasati_user');
    if (t && u) return { token: t, user: JSON.parse(u) };
  } catch (e) {}
  return null;
}

function clearAuth() {
  localStorage.removeItem('nasati_token');
  localStorage.removeItem('nasati_user');
}

function apiFetch(path, options = {}) {
  const auth = getStored();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (auth) headers['Authorization'] = 'Bearer ' + auth.token;
  return fetch(API + path, { ...options, headers });
}

function useIsPc() {
  const [isPc, setIsPc] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const onResize = () => setIsPc(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isPc;
}

function MainLayout({ children, auth, onLogout }) {
  const location = useLocation();
  const isPc = useIsPc();
  const isAdmin = auth?.user?.admin === 1 || auth?.user?.admin === true;
  const isAdminPage = location.pathname === '/admin';
  const [headerFamily, setHeaderFamily] = useState(null);
  useEffect(() => {
    const fid = auth?.user?.family_id;
    if (!fid) return;
    apiFetch(`/families/${fid}`)
      .then((r) => r.json())
      .then(setHeaderFamily)
      .catch(() => {});
  }, [auth?.user?.family_id]);
  return (
    <div className="nasati-page" style={styles.page} lang="he" dir="rtl">
      <div className="nasati-reports-container" style={styles.reportsContainer}>
        <header className="nasati-header" style={{ ...styles.header, ...(isPc ? styles.headerPc : {}) }}>
          {isPc ? (
            <>
              <Link to="/" style={styles.headerBrand}>
                <img src="/icon.png" alt="" style={styles.headerLogo} />
                <span style={styles.headerTitle}>Nasati</span>
              </Link>
              {(auth?.user?.name || headerFamily) && (
                <div style={styles.headerUserInfo}>
                  {auth?.user?.name && <span style={styles.userName}>{auth.user.name}</span>}
                  {auth?.user?.name && (headerFamily?.name || headerFamily?.inviteCode) && ' — '}
                  {headerFamily?.name ? `משפחת ${headerFamily.name}` : headerFamily ? 'משפחה' : ''}
                  {headerFamily?.inviteCode && (
                    <span style={styles.familyCode}> — קוד: {headerFamily.inviteCode}</span>
                  )}
                </div>
              )}
              <nav className="nasati-tabs" style={styles.tabs}>
                <Link to="/" style={{ ...styles.tabLink, ...(location.pathname === '/' && !isAdminPage ? styles.tabLinkActive : {}) }}>דוחות</Link>
                {isAdmin && (
                  <Link to="/admin" style={{ ...styles.tabLink, ...(location.pathname === '/admin' ? styles.tabLinkActive : {}) }}>ניהול</Link>
                )}
              </nav>
              <button onClick={() => { clearAuth(); onLogout(); }} style={styles.logoutBtn}>
                יציאה
              </button>
            </>
          ) : (
            <>
              <h1 className="nasati-title" style={styles.title}>
                {isAdminPage ? 'ניהול' : 'דוחות נסיעות'}
              </h1>
              {isAdmin && (
                <nav className="nasati-tabs" style={styles.tabs}>
                  <Link to="/" style={{ ...styles.tabLink, ...(location.pathname === '/' ? styles.tabLinkActive : {}) }}>דוחות</Link>
                  <Link to="/admin" style={{ ...styles.tabLink, ...(location.pathname === '/admin' ? styles.tabLinkActive : {}) }}>ניהול</Link>
                </nav>
              )}
              <button onClick={() => { clearAuth(); onLogout(); }} style={styles.logoutBtn}>
                יציאה
              </button>
            </>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}

function Reports({ onLogout, onProfileSaved }) {
  const auth = getStored();
  const familyId = auth?.user?.family_id;
  const [trips, setTrips] = useState([]);
  const [chartTrips, setChartTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [reportVehicles, setReportVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [family, setFamily] = useState(null);
  const [activeTrips, setActiveTrips] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [loading, setLoading] = useState(false);
  const formatDateInput = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const [from, setFrom] = useState(() => {
    const today = new Date();
    const fromDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
    return formatDateInput(fromDate);
  });
  const [to, setTo] = useState(() => {
    const today = new Date();
    return formatDateInput(today);
  });
  const [newCarName, setNewCarName] = useState('');
  const [newCarPlate, setNewCarPlate] = useState('');
  const [newCarOwnerId, setNewCarOwnerId] = useState('');
  const [addCarError, setAddCarError] = useState('');
  const [addCarLoading, setAddCarLoading] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('אבא');
  const [addMemberError, setAddMemberError] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [profileName, setProfileName] = useState(auth?.user?.name ?? '');
  const [profilePhone, setProfilePhone] = useState(auth?.user?.phone ?? '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [reportsTab, setReportsTab] = useState('family');

  const founderUserId = useMemo(() => {
    if (!users?.length) return null;
    const sorted = [...users].sort(
      (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
    );
    return sorted[0]?.id ?? null;
  }, [users]);

  const canRemoveVehicle = useCallback(
    (vehicle) => {
      const uid = auth?.user?.id;
      if (!uid || !vehicle) return false;
      if (vehicle.created_by_user_id && String(vehicle.created_by_user_id) === String(uid)) {
        return true;
      }
      return founderUserId != null && String(founderUserId) === String(uid);
    },
    [auth?.user?.id, founderUserId]
  );

  const refreshVehiclesAndUsers = useCallback(() => {
    if (!familyId) return;
    Promise.all([
      apiFetch(`/families/${familyId}/trips/active`).then((r) => r.json()).catch(() => []),
      apiFetch(`/families/${familyId}/vehicles`).then((r) => r.json()).catch(() => []),
      apiFetch(`/families/${familyId}/vehicles?includeRemoved=1`).then((r) => r.json()).catch(() => []),
      apiFetch(`/families/${familyId}/users`).then((r) => r.json()).catch(() => []),
    ]).then(([active, veh, allVeh, usr]) => {
      setActiveTrips(Array.isArray(active) ? active : []);
      setVehicles(Array.isArray(veh) ? veh : []);
      setReportVehicles(Array.isArray(allVeh) ? allVeh : []);
      setUsers(Array.isArray(usr) ? usr : []);
    });
  }, [familyId]);

  useEffect(() => {
    if (!familyId) return;
    apiFetch(`/families/${familyId}`)
      .then((r) => r.json())
      .then(setFamily)
      .catch(() => {});
    refreshVehiclesAndUsers();
  }, [familyId, refreshVehiclesAndUsers]);

  const loadChartTrips = useCallback(() => {
    if (!familyId) return;
    const today = new Date();
    const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    const fromStr = formatDateInput(threeMonthsAgo);
    const toStr = formatDateInput(today);
    const q = new URLSearchParams();
    q.set('familyId', familyId);
    q.set('from', fromStr + ' 00:00:01');
    q.set('to', toStr + ' 23:59:59');
    apiFetch('/trips?' + q)
      .then((r) => r.json())
      .then((data) => setChartTrips(Array.isArray(data) ? data : []))
      .catch(() => setChartTrips([]));
  }, [familyId]);

  useEffect(() => {
    if (familyId) loadChartTrips();
  }, [familyId, loadChartTrips]);

  useEffect(() => {
    if (auth?.user) {
      setProfileName(auth.user.name ?? '');
      setProfilePhone(auth.user.phone ?? '');
    }
  }, [auth?.user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!auth?.user?.id) return;
    setProfileError('');
    setProfileSaving(true);
    try {
      const res = await apiFetch(`/users/${auth.user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: profileName.trim(), phone: profilePhone.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'עדכון נכשל');
      localStorage.setItem('nasati_user', JSON.stringify({ ...auth.user, ...data }));
      onProfileSaved?.();
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const ROLES = [
    { value: 'אבא', label: 'אבא' },
    { value: 'אמא', label: 'אמא' },
    { value: 'בן', label: 'בן' },
    { value: 'בת', label: 'בת' },
    { value: 'אחר', label: 'אחר' },
  ];

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddMemberError('');
    const trimmed = (newMemberName || '').trim();
    if (!trimmed || trimmed.length < 2) {
      setAddMemberError('השם חייב להכיל לפחות 2 תווים');
      return;
    }
    setAddMemberLoading(true);
    try {
      const res = await apiFetch(`/families/${familyId}/users`, {
        method: 'POST',
        body: JSON.stringify({ name: trimmed, role: newMemberRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'הוספה נכשלה');
      setNewMemberName('');
      setNewMemberRole('אבא');
      refreshVehiclesAndUsers();
    } catch (err) {
      setAddMemberError(err.message);
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setAddCarError('');
    const plateNorm = normalizePlate(newCarPlate || '');
    const plateExists = (reportVehicles.length ? reportVehicles : vehicles || []).some(
      (v) => normalizePlate(v.plate || '') === plateNorm && plateNorm !== ''
    );
    if (plateExists) {
      setAddCarError('מספר רישוי זה כבר קיים במשפחה. הזן מספר רישוי אחר.');
      setNewCarPlate('');
      return;
    }
    setAddCarLoading(true);
    try {
      const res = await apiFetch(`/families/${familyId}/vehicles`, {
        method: 'POST',
        body: JSON.stringify({
          name: newCarName.trim(),
          plate: plateNorm || (newCarPlate || '').trim(),
          ownerId: newCarOwnerId || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setNewCarName('');
      setNewCarPlate('');
      setNewCarOwnerId('');
      refreshVehiclesAndUsers();
    } catch (err) {
      setAddCarError(err.message);
    } finally {
      setAddCarLoading(false);
    }
  };

  const handleRemoveVehicle = async (vehicle) => {
    if (!vehicle?.id || !familyId) return;
    const label = `${vehicle.name} (${formatPlate(vehicle.plate || '')})`;
    if (!window.confirm(`להסיר את ${label} מרשימת הרכבים הפעילים?\nהרכב יישאר זמין לבחירה בדוחות.`)) return;
    try {
      const res = await apiFetch(`/families/${familyId}/vehicles/${vehicle.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'הסרת הרכב נכשלה');
      if (vehicleId === vehicle.id) setVehicleId('');
      refreshVehiclesAndUsers();
    } catch (err) {
      window.alert(err.message);
    }
  };

  const loadTrips = async () => {
    if (!familyId) return;
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (familyId) q.set('familyId', familyId);
      if (from) q.set('from', from + ' 00:00:01');
      if (to) q.set('to', to + ' 23:59:59');
      if (vehicleId) q.set('vehicleId', vehicleId);
      if (driverId) q.set('driverId', driverId);
      const res = await apiFetch('/trips?' + q);
      const data = await res.json();
      const tripList = Array.isArray(data) ? data : [];
      setTrips(tripList);
      setChartTrips(tripList);
    } catch (e) {
      setTrips([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const CHART_COLORS = ['#f59e0b', '#f97316', '#fb923c', '#fbbf24', '#34d399', '#22d3ee', '#a78bfa', '#f472b6'];
  const chartWidth = 360;
  const chartHeight = 220;
  const chartHeightPerCar = Math.round(chartHeight * 0.7);

  const { byVehicleStacked, vehicleDrivers, byDriver } = useMemo(() => {
    const completed = chartTrips.filter((t) => t.start_time && t.end_time);
    const tripMinutes = (t) => (new Date(t.end_time) - new Date(t.start_time)) / 60000;
    const byVehicleDriversMap = {};
    const byDriverMap = {};
    const driverSet = new Set();
    completed.forEach((t) => {
      const min = Math.max(0, tripMinutes(t));
      const vLabel = `${t.vehicle_name || ''} ${formatPlate(t.vehicle_plate || '')}`.trim() || 'רכב';
      const dLabel = t.driver_name || 'נהג';
      driverSet.add(dLabel);
      if (!byVehicleDriversMap[vLabel]) byVehicleDriversMap[vLabel] = {};
      byVehicleDriversMap[vLabel][dLabel] = (byVehicleDriversMap[vLabel][dLabel] || 0) + min;
      byDriverMap[dLabel] = (byDriverMap[dLabel] || 0) + min;
    });
    const vehicleDrivers = Array.from(driverSet);
    const byVehicleStacked = Object.entries(byVehicleDriversMap).map(([carName, drivers]) => {
      const row = { name: carName };
      vehicleDrivers.forEach((d) => { row[d] = drivers[d] || 0; });
      return row;
    });
    const byDriver = Object.entries(byDriverMap).map(([name, value]) => ({ name, value }));
    return { byVehicleStacked, vehicleDrivers, byDriver };
  }, [chartTrips]);

  const formatTooltip = (v) => {
    const h = Math.floor(v / 60);
    const m = Math.round(v % 60);
    return h > 0 ? `${h} שע׳ ${m} דק׳` : `${m} דק׳`;
  };
  const yAxisHours = (v) => (v / 60).toFixed(1) + ' שע׳';

  return (
    <>
        <div style={styles.reportsTabs}>
          <button
            type="button"
            style={{ ...styles.reportsTabBtn, ...(reportsTab === 'family' ? styles.reportsTabBtnActive : {}) }}
            onClick={() => setReportsTab('family')}
          >
            משפחה
          </button>
          <button
            type="button"
            style={{ ...styles.reportsTabBtn, ...(reportsTab === 'reports' ? styles.reportsTabBtnActive : {}) }}
            onClick={() => setReportsTab('reports')}
          >
            דוחות
          </button>
        </div>
        {reportsTab === 'family' && (
          <>
            {(family || auth?.user?.name) && (
              <p style={styles.familyName}>
                {auth?.user?.name && (
                  <span style={styles.userName}>{auth.user.name}</span>
                )}
                {auth?.user?.name && (family?.name || family?.inviteCode) && ' — '}
                {family?.name ? `משפחת ${family.name}` : family ? 'משפחה' : ''}
                {family?.inviteCode && (
                  <span style={styles.familyCode}> — קוד: {family.inviteCode}</span>
                )}
              </p>
            )}
            <section className="nasati-section" style={styles.section}>
          <h2 className="nasati-section-title" style={styles.sectionTitle}>הפרופיל שלי</h2>
          <form onSubmit={handleSaveProfile} className="nasati-add-form" style={styles.addForm}>
            <div className="nasati-add-form-fields" style={styles.addFormFields}>
              <input
                type="text"
                placeholder="שם"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                className="nasati-filter-input nasati-input"
                style={styles.filterInput}
                required
              />
              <div style={{ ...styles.filterField, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <label style={styles.filterLabel} htmlFor="profile-phone">טלפון (אופציונלי)</label>
                <input
                  id="profile-phone"
                  type="tel"
                  value={profilePhone}
                  onChange={(e) => setProfilePhone(e.target.value)}
                  className="nasati-filter-input nasati-input"
                  style={styles.filterInput}
                />
              </div>
            </div>
            <button type="submit" className="nasati-button" style={styles.button} disabled={profileSaving}>
              {profileSaving ? '...' : 'שמור פרופיל'}
            </button>
          </form>
          {profileError && <p style={styles.errorText}>{profileError}</p>}
        </section>

        <section className="nasati-section" style={styles.section}>
          <h2 className="nasati-section-title" style={styles.sectionTitle}>חברי משפחה</h2>
          {family?.inviteCode && (
            <p style={styles.inviteCode}>
              קוד הזמנה להצטרפות: <strong style={styles.code}>{family.inviteCode}</strong>
              <span style={styles.inviteHint}> — שתף את הקוד עם חברי המשפחה כדי שיוכלו להצטרף באפליקציה או בהרשמה באתר</span>
            </p>
          )}
          <ul style={styles.userList}>
            {users.map((u) => {
              const userActiveTrip = activeTrips.find((t) => String(t.driver_id) === String(u.id));
              const drivingVehicle = userActiveTrip ? `${userActiveTrip.vehicle_name || ''} ${formatPlate(userActiveTrip.vehicle_plate || '')}`.trim() : null;
              return (
                <li key={u.id} style={styles.userItem}>
                  {u.name}{u.role ? ` (${u.role})` : ''}{u.email ? ` — ${u.email}` : ''}{u.phone ? ` — ${u.phone}` : ''} — <span style={u.registered ? styles.registeredBadge : styles.pendingBadge}>{u.registered ? 'רשום' : 'ממתין להצטרפות'}</span>
                  {drivingVehicle && (
                    <span style={styles.userDrivingVehicle}> — נוהג ב: {drivingVehicle}</span>
                  )}
                </li>
              );
            })}
          </ul>
          <form onSubmit={handleAddMember} className="nasati-add-form" style={styles.addForm}>
            <div className="nasati-add-form-fields" style={styles.addFormFields}>
              <input
                type="text"
                placeholder="שם בן משפחה"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                className="nasati-filter-input nasati-input"
                style={styles.filterInput}
              />
              <div style={styles.roleRadioGroup}>
                {ROLES.map((r) => (
                  <label key={r.value} style={styles.roleRadioLabel}>
                    <input
                      type="radio"
                      name="newMemberRole"
                      value={r.value}
                      checked={newMemberRole === r.value}
                      onChange={(e) => setNewMemberRole(e.target.value)}
                      style={styles.roleRadioInput}
                    />
                    <span style={styles.roleRadioText}>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" className="nasati-button" style={styles.button} disabled={addMemberLoading}>
              {addMemberLoading ? '...' : 'הוסף בן משפחה'}
            </button>
          </form>
          {addMemberError && <p style={styles.errorText}>{addMemberError}</p>}
          <p style={styles.inviteHint}>בני משפחה שנוספו כאן יוכלו לבחור את עצמם מרשימה כשמצטרפים לאפליקציה עם קוד ההזמנה.</p>
        </section>

        <section className="nasati-section" style={styles.section}>
          <h2 className="nasati-section-title" style={styles.sectionTitle}>רכבים</h2>
          <div style={styles.carsAndChartWrap}>
            <div style={styles.carsListWrap}>
              <ul style={styles.userList}>
                {vehicles.map((v) => {
                  const activeTrip = activeTrips.find((t) => String(t.vehicle_id) === String(v.id));
                  const inUse = !!activeTrip;
                  return (
                    <li key={v.id} style={styles.userItem}>
                      <span>
                        {v.name} — {formatPlate(v.plate)}{v.owner_name ? ` (${v.owner_name})` : ''}
                        {' — '}
                        <span style={inUse ? styles.vehicleStatusInUse : styles.vehicleStatusAvailable}>
                          {inUse ? `בשימוש ע"י ${activeTrip.driver_name || '—'}` : 'פנוי'}
                        </span>
                      </span>
                      {canRemoveVehicle(v) ? (
                        <button
                          type="button"
                          className="nasati-button-secondary"
                          style={styles.removeVehicleBtn}
                          disabled={inUse}
                          title={inUse ? 'לא ניתן להסיר רכב בשימוש' : 'הסר מהרשימה (נשאר בדוחות)'}
                          onClick={() => handleRemoveVehicle(v)}
                        >
                          הסר
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {vehicles.length === 0 && <p style={styles.inviteHint}>אין רכבים.</p>}
            </div>
            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>זמן נסיעה לפי רכב</div>
              {byVehicleStacked.length > 0 ? (
                <ResponsiveContainer width={chartWidth} height={chartHeightPerCar}>
                  <BarChart data={byVehicleStacked} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={yAxisHours} style={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v) => formatTooltip(v)}
                      offset={-60}
                      wrapperStyle={{ zIndex: 1000 }}
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '8px 12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                      }}
                    />
                    <Legend />
                    {vehicleDrivers.map((driver, i) => (
                      <Bar key={driver} dataKey={driver} stackId="car" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={i === vehicleDrivers.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={styles.chartEmpty}>אין נתונים בפרק הזמן</div>
              )}
            </div>
          </div>
          <form onSubmit={handleAddVehicle} className="nasati-add-form" style={styles.addForm}>
            <div className="nasati-add-form-fields" style={styles.addFormFields}>
              <input
                type="text"
                placeholder="שם הרכב"
                value={newCarName}
                onChange={(e) => setNewCarName(e.target.value)}
                className="nasati-filter-input nasati-input"
                style={styles.filterInput}
                required
              />
              <input
                type="text"
                placeholder="מספר רישוי"
                value={newCarPlate}
                onChange={(e) => setNewCarPlate(e.target.value)}
                className="nasati-filter-input nasati-input"
                style={styles.filterInput}
                required
              />
              <select
                value={newCarOwnerId}
                onChange={(e) => setNewCarOwnerId(e.target.value)}
                className="nasati-filter-input nasati-input"
                style={styles.filterInput}
              >
                <option value="">ללא בעלים</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="nasati-button" style={styles.button} disabled={addCarLoading}>
              {addCarLoading ? '...' : 'הוסף רכב'}
            </button>
          </form>
          {addCarError && <p style={styles.errorText}>{addCarError}</p>}
        </section>
          </>
        )}
        {reportsTab === 'reports' && (
          <>
        <div className="nasati-filters" style={styles.filters}>
          <div className="nasati-filters-fields" style={styles.filtersFields}>
            <div style={styles.filterField}>
              <span style={styles.filterLabel}>מתאריך</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="nasati-filter-input nasati-input"
                style={styles.filterInput}
                aria-label="מתאריך"
              />
            </div>
            <div style={styles.filterField}>
              <span style={styles.filterLabel}>עד תאריך</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="nasati-filter-input nasati-input"
                style={styles.filterInput}
                aria-label="עד תאריך"
              />
            </div>
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className="nasati-filter-input nasati-input"
              style={styles.filterInput}
            >
              <option value="">כל הרכבים</option>
              {(reportVehicles.length ? reportVehicles : vehicles).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({formatPlate(v.plate)}){v.removed_at ? ' — הוסר' : ''}
                </option>
              ))}
            </select>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              className="nasati-filter-input nasati-input"
              style={styles.filterInput}
            >
              <option value="">כל הנהגים</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} {u.role ? '(' + u.role + ')' : ''}</option>
              ))}
            </select>
          </div>
          <button onClick={loadTrips} className="nasati-button" style={styles.button} disabled={loading}>
            {loading ? 'טוען...' : 'הפק דוח'}
          </button>
        </div>

        {byDriver.length > 0 && (
          <div style={styles.chartsRow}>
              <div style={styles.chartCard}>
                <div style={styles.chartTitle}>זמן נסיעה לפי נהג</div>
                <ResponsiveContainer width={chartWidth} height={chartHeight}>
                  <BarChart data={byDriver} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={yAxisHours} style={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatTooltip(v)} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {byDriver.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
          </div>
        )}

        <div className="nasati-table-wrap" style={styles.tableWrap}>
          {trips.length > 0 && (
            <p style={styles.tripCount}>נמצאו {trips.length} נסיעות</p>
          )}
          <div style={styles.tableScrollWrap}>
            <table className="nasati-table" style={styles.table}>
              <thead style={styles.tableThead}>
                <tr>
                <th className="nasati-table-cell" style={styles.tableCell}>רכב</th>
                <th className="nasati-table-cell" style={styles.tableCell}>נהג</th>
                <th className="nasati-table-cell" style={styles.tableCell}>התחלה</th>
                <th className="nasati-table-cell" style={styles.tableCell}>סיום</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id}>
                  <td className="nasati-table-cell" style={styles.tableCell}>{t.vehicle_name} {formatPlate(t.vehicle_plate || '')}</td>
                  <td className="nasati-table-cell" style={styles.tableCell}>{t.driver_name}</td>
                  <td className="nasati-table-cell" style={styles.tableCell}>{formatDate(t.start_time)}</td>
                  <td className="nasati-table-cell" style={styles.tableCell}>{formatDate(t.end_time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        {trips.length === 0 && !loading && (
          <p style={styles.hint}>בחר סינון ולחץ "הפק דוח"</p>
        )}
          </>
        )}
    </>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f0fdfa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, sans-serif',
    direction: 'rtl',
    textAlign: 'right',
  },
  title: { color: '#0f766e', fontSize: 28, marginBottom: 8, textAlign: 'right' },
  button: {
    background: '#0f766e',
    color: '#fff',
    border: 'none',
    padding: 14,
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'right',
  },
  reportsContainer: { width: '100%', maxWidth: 1200, padding: 24, direction: 'rtl', textAlign: 'right' },
  reportsTabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
    direction: 'rtl',
  },
  reportsTabBtn: {
    padding: '10px 20px',
    fontSize: 15,
    fontWeight: 600,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    background: '#fff',
    color: '#64748b',
    cursor: 'pointer',
  },
  reportsTabBtnActive: {
    background: 'rgba(15, 118, 110, 0.15)',
    color: '#0f766e',
    borderColor: '#0f766e',
  },
  familyName: { color: '#0f766e', fontSize: 20, fontWeight: 700, marginBottom: 16, textAlign: 'right' },
  familyCode: { color: '#64748b', fontWeight: 500, fontFamily: 'monospace', letterSpacing: 1 },
  section: { marginBottom: 24, textAlign: 'right' },
  sectionTitle: { color: '#0f766e', fontSize: 18, marginBottom: 12, textAlign: 'right' },
  addForm: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    direction: 'rtl',
  },
  addFormFields: {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    direction: 'rtl',
    minWidth: 200,
  },
  errorText: { color: '#dc2626', fontSize: 14, marginTop: 8, textAlign: 'right' },
  roleRadioGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    direction: 'rtl',
  },
  roleRadioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
  },
  roleRadioInput: { cursor: 'pointer' },
  roleRadioText: { fontSize: 14, color: '#334155' },
  inviteCode: { marginBottom: 8, color: '#475569', fontSize: 14, textAlign: 'right' },
  code: { fontFamily: 'monospace', letterSpacing: 2, color: '#0f766e' },
  inviteHint: { fontWeight: 'normal', color: '#94a3b8' },
  userList: { listStyle: 'none', padding: 0, margin: 0, textAlign: 'right' },
  userItem: {
    padding: '6px 0',
    borderBottom: '1px solid #e2e8f0',
    color: '#334155',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  removeVehicleBtn: {
    padding: '4px 10px',
    fontSize: 13,
    flexShrink: 0,
    background: '#fff',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    borderRadius: 6,
    cursor: 'pointer',
  },
  registeredBadge: { color: '#16a34a', fontWeight: 600 },
  pendingBadge: { color: '#94a3b8', fontStyle: 'italic' },
  vehicleStatusAvailable: { color: '#16a34a', fontWeight: 600, marginRight: 8 },
  vehicleStatusInUse: { color: '#ea580c', fontWeight: 600, marginRight: 8 },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 24,
    flexDirection: 'row-reverse',
    direction: 'ltr',
    textAlign: 'right',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: '#f0fdfa',
    padding: '12px 0',
    marginLeft: -24,
    marginRight: -24,
    paddingLeft: 24,
    paddingRight: 24,
  },
  headerPc: { justifyContent: 'space-between', flexWrap: 'wrap' },
  headerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    textDecoration: 'none',
    color: '#0f766e',
    fontWeight: 700,
  },
  headerLogo: { width: 32, height: 32, objectFit: 'contain' },
  headerTitle: { fontSize: 20 },
  headerUserInfo: { fontSize: 14, color: '#475569', flex: 1, textAlign: 'center', minWidth: 120 },
  userName: { color: '#0f766e', fontWeight: 600 },
  tabs: { display: 'flex', gap: 12, direction: 'ltr' },
  tabLink: {
    color: '#64748b',
    textDecoration: 'none',
    fontSize: 16,
    fontWeight: 600,
    padding: '8px 12px',
    borderRadius: 8,
  },
  tabLinkActive: { color: '#0f766e', background: 'rgba(15, 118, 110, 0.15)' },
  logoutBtn: {
    marginRight: 'auto',
    background: 'transparent',
    color: '#64748b',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
  },
  filters: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
    alignItems: 'center',
    direction: 'rtl',
  },
  filtersFields: {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
    direction: 'rtl',
    minWidth: 200,
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    direction: 'rtl',
    textAlign: 'right',
  },
  filterLabel: {
    display: 'block',
    fontSize: 14,
    color: '#334155',
    fontWeight: 600,
    textAlign: 'right',
  },
  filterInput: {
    padding: 10,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontSize: 14,
    textAlign: 'right',
    direction: 'rtl',
  },
  carsAndChartWrap: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    alignItems: 'flex-start',
    direction: 'rtl',
    marginBottom: 16,
  },
  carsListWrap: { flex: 1, minWidth: 200 },
  chartsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24,
    marginBottom: 24,
    direction: 'rtl',
  },
  chartCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#334155',
    textAlign: 'right',
  },
  chartEmpty: {
    width: 360,
    height: 154,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
    fontSize: 14,
  },
  tableWrap: { marginBottom: 24 },
  tripCount: { fontSize: 14, color: '#64748b', marginBottom: 8, textAlign: 'right' },
  tableScrollWrap: { maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' },
  tableThead: { position: 'sticky', top: 0, zIndex: 1, background: '#0f766e' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    direction: 'rtl',
  },
  tableHeader: { position: 'sticky', top: 0, background: '#0f766e', color: '#fff', zIndex: 10 },
  tableCell: { textAlign: 'right', padding: '12px 16px' },
  hint: { color: '#94a3b8', marginTop: 16, textAlign: 'right' },
};

export default function App() {
  const [auth, setAuth] = useState(getStored);
  if (typeof document !== 'undefined') document.title = 'Nasati';

  useEffect(() => {
    setAuth(getStored());
  }, []);

  useEffect(() => {
    document.title = 'Nasati';
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={auth ? <MainLayout auth={auth} onLogout={() => setAuth(null)}><Reports onProfileSaved={() => setAuth(getStored())} /></MainLayout> : <Landing />} />
        <Route path="/admin" element={auth ? (auth.user?.admin ? <MainLayout auth={auth} onLogout={() => setAuth(null)}><AdminPage /></MainLayout> : <Navigate to="/" replace />) : <Navigate to="/login" replace />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Terms />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={auth ? <Navigate to="/" replace /> : <Login onLogin={() => setAuth(getStored())} />} />
        <Route path="/reports" element={auth ? <MainLayout auth={auth} onLogout={() => setAuth(null)}><Reports onProfileSaved={() => setAuth(getStored())} /></MainLayout> : <Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
